import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AnimationComponent } from '@finder/shared/components/animations/animation/animation.component';
import { AppOverviewComponent } from '@finder/shared/components/app-overview/app-overview.component';
import { AvailabilityCalendarComponent } from '@finder/shared/components/availability-calendar/availability-calendar.component';
import { AvatarListComponent } from '@finder/shared/components/avatar-list/avatar-list.component';
import { BgImageDirective } from '@finder/shared/directives/bg-image/bg-image.directive';
import { BlockWrapperComponent } from './block-wrapper/block-wrapper.component';
import { CampaignCtaComponent } from '@finder/shared/components/campaign-cta/campaign-cta.component';
import { CampaignDrawerGroupComponent } from '@finder/shared/components/campaign-drawer-group/campaign-drawer-group.component';
import { CampaignDrawerGroupMultiRowComponent } from '@finder/shared/components/campaign-drawer-infographic-multi-row/campaign-drawer-group-multi-row.component';
import { CampaignDrawerInfographicComponent } from '@finder/shared/components/campaign-drawer-infographic/campaign-drawer-infographic.component';
import { CampaignDrawerLinkGroupComponent } from '@finder/shared/components/campaign-drawer-link-group/campaign-drawer-link-group.component';
import { CampaignLottieComponent } from '@finder/shared/components/campaign-lottie/campaign-lottie.component';
import { CampaignMapComponent } from '@finder/shared/components/campaign-map/campaign-map.component';
import { CarouselComponent } from '@finder/shared/components/carousel/carousel.component';
import { CmsMarginDirective } from '@finder/shared/directives/cms-margin/cms-margin.directive';
import { CollapsibleTextComponent } from '@finder/shared/components/collapsible-text/collapsible-text.component';
import { DescriptiveTextComponent } from '@finder/shared/components/descriptive-text/descriptive-text.component';
import { EmailCaptureComponent } from '@finder/shared/components/email-capture/email-capture.component';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { GenericTextComponent } from '@finder/shared/components/generic-text/generic-text.component';
import { GenericTextCtaComponent } from '@finder/shared/components/generic-text-cta/generic-text-cta.component';
import { HorizontalRuleComponent } from '@finder/shared/components/horizontal-rule/horizontal-rule.component';
import { IframeComponent } from '@finder/shared/components/iframe/iframe.component';
import { ImageBlockComponent } from '@finder/shared/components/image-block/image-block.component';
import { ImageDrawerComponent } from '@finder/shared/components/image-drawer/image-drawer.component';
import { InfographicComponent } from '@finder/shared/components/infographic/infographic.component';
import { IntersectionDirective } from '@finder/shared/directives/intersection/intersection.directive';
import { InViewportDirective } from '@finder/shared/directives/in-viewport/in-viewport.directive';
import { KebabCasePipe } from '@finder/shared/utils/pipes/kebab-case/kebab-case.pipe';
import { ListBlockComponent } from '@finder/shared/components/list-block/list-block.component';
import { LivePersonComponent } from '@finder/shared/components/live-person/live-person.component';
import { MediaDrawerComponent } from '@finder/shared/components/media-drawer/media-drawer.component';
import { MultiColumnTextComponent } from '@finder/shared/components/multi-column-text/multi-column-text.component';
import { MultiCtaComponent } from '@finder/shared/components/multi-cta/multi-cta.component';
import { OverlayElementDirective } from '@finder/shared/directives/overlay-element/overlay-element.directive';
import { ParallaxBackgroundComponent } from '@finder/shared/components/parallax-background/parallax-background.component';
import { ParallaxBackgroundService } from '@finder/shared/components/parallax-background/parallax-background.service';
import { ParallaxImageComponent } from '@finder/shared/components/parallax-image/parallax-image.component';
import { PartialBgDirective } from '@finder/shared/directives/partial-bg/partial-bg.directive';
import { PassDetailCardComponent } from '@finder/shared/components/pass-detail-card/pass-detail-card.component';
import { PolylineComponent } from '@finder/shared/components/polyline/polyline.component';
import { PricingGridComponent } from '@finder/shared/components/pricing-grid/pricing-grid.component';
import { ProductsGroupComponent } from '@finder/shared/components/products-group/products-group.component';
import { PromoBannerComponent } from '@finder/shared/components/promo-banner/promo-banner.component';
import { SamplePackagesComponent } from '@finder/shared/components/sample-packages/sample-packages.component';
import { ScrollToDirective } from '@finder/shared/directives/scroll-to/scroll-to.directive';
import { SharedTableComponent } from '@finder/shared/components/table/table.component';
import { TabComponent } from '@finder/shared/components/tab/tab.component';
import { TimelineImageComponent } from '@finder/shared/components/timeline-image/timeline-image.component';
import { TutorialComponent } from '@finder/shared/components/tutorial/tutorial.component';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';

@Component({
    selector: 'finder-block-layout-renderer',
    templateUrl: './block-layout-renderer.component.html',
    styleUrls: ['./block-layout-renderer.component.scss'],
    standalone: true,
    imports: [
        AnimationComponent,
        AppOverviewComponent,
        AvailabilityCalendarComponent,
        AvatarListComponent,
        BgImageDirective, // finderBgImage
        BlockWrapperComponent,
        CampaignCtaComponent,
        CampaignDrawerGroupComponent,
        CampaignDrawerInfographicComponent,
        CampaignDrawerLinkGroupComponent,
        CampaignLottieComponent,
        CampaignMapComponent,
        CarouselComponent,
        CmsMarginDirective, // finderCmsMargin
        CollapsibleTextComponent,
        CommonModule,
        DescriptiveTextComponent,
        EmailCaptureComponent,
        GenericTextComponent,
        GenericTextCtaComponent,
        HorizontalRuleComponent,
        IframeComponent,
        ImageBlockComponent,
        ImageDrawerComponent,
        InfographicComponent,
        IntersectionDirective, // finderIntersection
        InViewportDirective, // finderInViewport
        KebabCasePipe,
        ListBlockComponent,
        LivePersonComponent,
        MediaDrawerComponent,
        MultiColumnTextComponent,
        MultiCtaComponent,
        OverlayElementDirective, // finderOverlayElement
        ParallaxBackgroundComponent,
        ParallaxImageComponent,
        PartialBgDirective, // finderPartialBg
        PassDetailCardComponent,
        PolylineComponent,
        PricingGridComponent,
        ProductsGroupComponent,
        PromoBannerComponent,
        SamplePackagesComponent,
        ScrollToDirective, // finderScrollTo
        SharedTableComponent,
        TabComponent,
        TimelineImageComponent,
        TutorialComponent,
        CampaignDrawerGroupMultiRowComponent,
    ]
})
export class BlockLayoutRendererComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() blocks;
    @Input() theme;
    @Input() disableDefaultSpacing: boolean;

    private destroy$ = new Subject<void>();

    isMobile: boolean;

    constructor(
        private parallaxBgService: ParallaxBackgroundService,
        private finderAnalyticsService: FinderAnalyticsService,
        private viewportService: ViewportDetectionService,
    ) {}

    ngOnDestroy() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit() {
        this.isMobile = this.viewportService.isMobile();
    }

    ngAfterViewInit() {
        this.viewportService.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.isMobile = this.viewportService.isMobile();
            });
    }

    checkForIntersection(ioEntry: IntersectionObserverEntry) {
        this.parallaxBgService.handleIntersection(ioEntry);
    }

    analyticsFireTrackSeen(linkId) {
        if (linkId) {
            this.finderAnalyticsService.updateModelAndTrack({linkId});
        }
    }
}
